import React from 'react';
import Icon from '../../../components/AppIcon';
import reviewAutomationService from '../../../utils/reviewAutomationService';
import { useOrganization } from '../../../contexts/OrganizationContext';

const ReviewStatusIndicator = ({ customerId, className = '', size = 'default', showDetails = false }) => {
  const { currentOrganization } = useOrganization();
  const [reviewStatus, setReviewStatus] = React.useState({
    status: 'none',
    actionAvailable: false,
    automationSettings: null,
    lastRequest: null,
    timeUntilAction: null,
    hoursSinceSent: null
  });

  React.useEffect(() => {
    const fetchStatus = async () => {
      try {
        console.log('🔍 ReviewStatusIndicator: Fetching status for customer ID:', customerId);
        console.log('🏢 ReviewStatusIndicator: Organization ID:', currentOrganization?.id);
        const result = await reviewAutomationService.getDetailedReviewStatus(customerId, currentOrganization?.id);
        console.log('✅ ReviewStatusIndicator: Result received:', result);
        
        if (result.success && result.data) {
          setReviewStatus(result.data);
        } else {
          console.error('❌ ReviewStatusIndicator: Failed to get status:', result.error);
          setReviewStatus({
            status: 'none',
            actionAvailable: false,
            automationSettings: null,
            lastRequest: null,
            timeUntilAction: null,
            hoursSinceSent: null
          });
        }
      } catch (error) {
        console.error('❌ ReviewStatusIndicator: Error fetching review status:', error);
        setReviewStatus({
          status: 'none',
          actionAvailable: false,
          automationSettings: null,
          lastRequest: null,
          timeUntilAction: null,
          hoursSinceSent: null
        });
      }
    };

    if (customerId && currentOrganization?.id) {
      fetchStatus();
    }
  }, [customerId, currentOrganization?.id]);

  // Size configurations for different contexts
  const sizeConfig = {
    sm: {
      iconSize: 12,
      padding: 'px-1.5 py-0.5',
      textSize: 'text-xs',
      spacing: 'mr-1'
    },
    default: {
      iconSize: 14,
      padding: 'px-2 py-1',
      textSize: 'text-xs',
      spacing: 'mr-1.5'
    },
    lg: {
      iconSize: 16,
      padding: 'px-3 py-1.5',
      textSize: 'text-sm',
      spacing: 'mr-2'
    }
  };

  const currentSize = sizeConfig[size] || sizeConfig.default;

  const getStatusConfig = (status, reviewStatus) => {
    const { timeUntilAction, hoursSinceSent, automationSettings } = reviewStatus;

    switch (status) {
      case 'pending':
        return {
          icon: 'Clock',
          color: 'text-amber-600',
          bgColor: 'bg-amber-50',
          borderColor: 'border-amber-200',
          text: 'Awaiting Send',
          tooltip: 'Review request created but not yet sent'
        };
      case 'submitted':
        const isAutomated = automationSettings?.reminderAutomation;
        const timeText = timeUntilAction > 0 ? ` (${timeUntilAction}h until follow-up)` : '';
        return {
          icon: 'Send',
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          text: isAutomated ? `Awaiting Feedback${timeText}` : 'Awaiting Feedback',
          tooltip: `Review request sent ${hoursSinceSent} hours ago${isAutomated && timeUntilAction > 0 ? `. Follow-up in ${timeUntilAction} hours` : ''}`
        };
      case 'follow_up_sent':
        return {
          icon: 'RotateCcw',
          color: 'text-purple-600',
          bgColor: 'bg-purple-50',
          borderColor: 'border-purple-200',
          text: 'Follow Up Sent',
          tooltip: `Follow-up reminder sent ${hoursSinceSent} hours ago`
        };
      case 'completed':
        const rating = reviewStatus.lastRequest?.review_rating;
        const ratingText = rating ? ` (${rating}★)` : '';
        return {
          icon: 'CheckCircle2',
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          text: `Review Complete${ratingText}`,
          tooltip: `Customer completed review${rating ? ` with ${rating} stars` : ''}`
        };
      case 'failed':
        return {
          icon: 'AlertCircle',
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          text: 'Send Failed',
          tooltip: 'Review request failed to send'
        };
      case 'expired':
        return {
          icon: 'Clock',
          color: 'text-gray-500',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          text: 'No Response',
          tooltip: 'Review request expired - no response received'
        };
      default:
        return {
          icon: 'MessageSquare',
          color: 'text-gray-400',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          text: 'No Request',
          tooltip: 'No review request sent yet'
        };
    }
  };

  const config = getStatusConfig(reviewStatus.status, reviewStatus);

  return (
    <div 
      className={`inline-flex items-center ${currentSize.padding} rounded-lg border ${config.bgColor} ${config.borderColor} ${className} transition-all duration-200 hover:shadow-sm`}
      title={config.tooltip}
    >
      <div className={`flex items-center justify-center w-5 h-5 rounded-full ${config.bgColor} ${config.borderColor} border`}>
        <Icon 
          name={config.icon} 
          size={currentSize.iconSize} 
          className={config.color} 
        />
      </div>
      <div className="flex items-center ml-2">
        <span className={`${currentSize.textSize} font-medium ${config.color}`}>
          {config.text}
        </span>
        {showDetails && (
          <div className="flex items-center ml-2 space-x-1">
            {reviewStatus.lastRequest?.trigger_type === 'manual' && (
              <span className="px-1 py-0.5 text-xs text-gray-500 bg-gray-100 rounded">Manual</span>
            )}
            {reviewStatus.lastRequest?.trigger_type === 'follow_up' && (
              <span className="px-1 py-0.5 text-xs text-purple-500 bg-purple-100 rounded">Follow-up</span>
            )}
            {reviewStatus.automationSettings?.reminderAutomation === false && reviewStatus.status === 'submitted' && (
              <span className="px-1 py-0.5 text-xs text-amber-500 bg-amber-100 rounded">Manual Mode</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewStatusIndicator; 